import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    AfterViewInit,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ViewChild,
    ElementRef
} from '@angular/core';

import { echarts, ECharts, EChartsCoreOption } from '../echarts.config';

@Component({
    selector: 'app-chart-pie',
    templateUrl: './chart-pie.component.html',
    styleUrl: './chart-pie.component.scss',
    standalone: false,
    // encapsulation: ViewEncapsulation.None
})
export class ChartPieComponent
    implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @Input() data: { name: string; y: number; color?: string }[] = [];
    @Input() title: string = '';
    @Input() subtitle: string = '';
    @Input() seriesName: string = 'Datos';
    @Input() colors: string[] = [];

    @Output() sliceClick = new EventEmitter<string>();

    @ViewChild('container', { static: true }) container!: ElementRef

    private chart!: ECharts;
    private themeObserver!: MutationObserver;
    private resizeObserver!: ResizeObserver;
    private selectedSlices = new Set<string>();

    ngOnInit(): void {
        this.syncDarkClass();
        this.themeObserver = new MutationObserver(() => {
            this.syncDarkClass();
            this.chart?.dispose();
            this.initChart();
        });
        this.themeObserver.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class']
        });
    }

    ngAfterViewInit(): void {
        this.initChart();
        this.resizeObserver = new ResizeObserver(() => this.chart?.resize());
        this.resizeObserver.observe(this.container.nativeElement);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.chart) return;
        if (changes['data'] || changes['colors']) {
            this.chart.setOption({ series: [{ data: this.buildData() }] });
        }
        if (changes['title'] || changes['subtitle']) {
            this.chart.setOption({
                title: { text: this.title, subtext: this.subtitle }
            });
        }
    }

    ngOnDestroy(): void {
        this.chart?.dispose();
        this.resizeObserver?.disconnect();
        this.themeObserver?.disconnect();
    }

    private syncDarkClass(): void {
        const isDark = document.documentElement.classList.contains('app-dark');
        document.body.classList.toggle('app-dark', isDark);
    }

    private isDark(): boolean {
        return document.documentElement.classList.contains('app-dark');
    }

    private theme() {
        const dark = this.isDark();
        return {
            text: dark ? '#e5e7eb' : '#374151',
            muted: dark ? '#9ca3af' : '#6b7280',
            tooltipBg: dark ? '#1f2937' : '#ffffff',
            tooltipBorder: dark ? '#374151' : '#e5e7eb',
        };
    }

    private buildData() {
        return this.data.map((item, i) => ({
            name: item.name,
            value: item.y,
            selected: this.selectedSlices.has(item.name),
            itemStyle: {
                color: this.colors[i] ?? item.color ?? undefined
            }
        }));
    }

    private initChart(): void {
        const el = this.container.nativeElement;
        const h = el.offsetHeight > 0 ? el.offsetHeight : 300;
        this.chart = echarts.init(el, null, { width: 'auto', height: h });
        this.chart.setOption(this.buildOptions());

        this.chart.on('click', (p: any) => {
            if (p.componentType !== 'series') return;
            const name: string = p.name;
            if (this.selectedSlices.has(name)) {
                this.selectedSlices.delete(name);
            } else {
                this.selectedSlices.clear();
                this.selectedSlices.add(name);
            }
            this.chart.setOption({ series: [{ data: this.buildData() }] });
            this.sliceClick.emit(name);
        });
    }

    private buildOptions(): EChartsCoreOption {
        const t = this.theme();
        return {
            backgroundColor: 'transparent',
            title: {
                text: this.title,
                subtext: this.subtitle,
                left: 'center',
                top: 5,
                itemGap: 9,
                textStyle: { color: t.text },
            },
            tooltip: {
                trigger: 'item',
                backgroundColor: t.tooltipBg,
                borderColor: t.tooltipBorder,
                borderWidth: 1,
                confine: true,
                textStyle: { color: t.text },
                formatter: (p: any) =>
                    `${p.seriesName}<br/><b>${p.name}</b>: ${p.value} (${p.percent.toFixed(1)}%)`
            },
            legend: {
                type: 'scroll',
                orient: 'horizontal',
                bottom: '2%',
                // left: 'center',
                pageIconColor: t.text,
                textStyle: { color: t.text },
                // padding: [5, 40],
                // height: 50
            },
            toolbox: {
                show: true,
                right: 16,
                top: 8,
                feature: {
                    saveAsImage: { name: 'grafico', title: 'Guardar' }
                },
                iconStyle: { borderColor: t.muted }
            },
            series: [{
                name: this.seriesName,
                type: 'pie',
                center: ['50%', '50%'],
                radius: ['25%', '58%'],
                avoidLabelOverlap: true,
                minAngle: 15,
                padAngle: 3, 
                selectedMode: 'single',
                selectedOffset: 10,
                itemStyle: {
                    borderWidth: 2,
                    borderColor: 'transparent',
                    borderRadius: 10
                },
                emphasis: {
                    scale: true,
                    scaleSize: 8,
                    itemStyle: {
                        shadowBlur: 12,
                        shadowColor: 'rgba(0,0,0,0.3)'
                    }
                },
                label: {
                    show: true,
                    position: 'outer',
                    alignTo: 'labelLine',
                    padding: [0, -10, 0, -10],
                    distanceToLabelLine: 8,
                    bleedMargin: 15,
                    formatter: (p: any) =>
                        `{b|${p.name}}\n{v|${p.value}}  {pct|(${p.percent.toFixed(1)}%)}`,
                    rich: {
                        b: { fontWeight: 'bold', color: t.text, fontSize: 12 },
                        v: { color: t.text, fontSize: 11 },
                        pct: { color: t.muted, fontSize: 11 },
                        overflow: 'break',
                        padding: [4, 0]
                    }
                },
                labelLayout: {
                    hideOverlap: true,
                    moveOverlap: 'shiftY'
                },
                labelLine: {
                    length: 10,
                    length2: 12,
                    smooth: 0.2,
                    minTurnAngle: 90,
                    maxSurfaceAngle: 40
                },
                animation: true,
                animationType: 'expansion',
                animationEasing: 'cubicOut',
                animationDuration: 1000,
                data: this.buildData()
            }]
        };
    }
}